import React, { useState, useEffect, useCallback, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormModal, IModalAction } from 'transfix-ui/components/Modals'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Typography } from 'transfix-ui/core'
import { submitNote } from 'utils/actions/notes'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { notesSelector } from 'utils/selectors/notes'
import { IAssignDriverFields, ICoverRequestData } from 'apps/booking/types'
import {
  saveCarrierToCover,
  saveDriverToCover,
  clearCoverloadError,
  clearCoverLoadModal,
} from 'apps/booking/utils/actions/coverLoad'
import {
  selectCoverLoadIsLoading,
  selectCoverLoadSuccess,
  isCoverLoadModalOpen,
  selectAssignDriverIsLoading,
  selectAssignedDriverSuccess,
  selectCoverLoadError,
  selectCoverloadNoteSubmitSuccess,
} from 'apps/booking/utils/selectors/coverLoad'
import { isPendingCoveredShipment } from 'apps/booking/utils/selectors/shipment'
import { activeRowIdSelector } from 'apps/booking/utils/selectors/sidebar'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { SuccessIcon } from 'transfix-icons'
import CardPanel from './CardPanel'
import CarrierEntryForm from './CarrierEntryForm'
import DriverAssignForm from './DriverAssignForm'

export const LoadingScreen = memo<{ isLoading: boolean }>(({ isLoading }) => (
  <div className='flex flex-grow-1 items-center justify-center relative cover-flow__loading'>
    {isLoading ? (
      <Spinner position='absolute' />
    ) : (
      <div className='flex flex-column items-center'>
        <SuccessIcon color='skyBlue6' size='xxlarge' className='db bounceIn' />
        <Typography variant='h2' className='mt4'>
          Load has been covered successfully!
        </Typography>
      </div>
    )}
  </div>
))

const CoverLoads = () => {
  const { trackEvent } = useMPAnalytics()
  const dispatch = useDispatch()

  const { userDetails: user } = useUserDetails()
  // Selectors
  const { activeRowId: shipmentId } = useSelector(activeRowIdSelector)
  const isModalOpen = useSelector(isCoverLoadModalOpen)
  const isLoadingStep1 = useSelector(selectCoverLoadIsLoading)
  const isLoadingStep2 = useSelector(selectAssignDriverIsLoading)
  const isCoverLoadSuccess = useSelector(selectCoverLoadSuccess)
  const isFormSubmitSuccess = useSelector(selectAssignedDriverSuccess)
  const isPendingCoveredStatus = useSelector(isPendingCoveredShipment)
  const isNoteSubmitSuccess = useSelector(selectCoverloadNoteSubmitSuccess)
  const coverLoadError = useSelector(selectCoverLoadError)
  const { notesError: notesCallbackError } = useSelector(notesSelector)

  // Local state
  const [showDriverEntry, setShowDriverEntry] = useState(false)
  const [coverFields, setCoverFields] = useState<ICoverRequestData | undefined>(undefined)
  const [updateFields, setUpdateFields] = useState<IAssignDriverFields | undefined>(undefined)
  const [assignNow, setAssignNow] = useState(true)
  const [callbackRecommended, setCallbackRecommended] = useState(true)

  const selectAssignOption = () => setAssignNow(!assignNow)
  const selectCallbackOption = () => setCallbackRecommended(!callbackRecommended)

  // Display state
  const showCarrierEntry =
    !isLoadingStep1 && !isCoverLoadSuccess && !isPendingCoveredStatus && isModalOpen
  const showSuccessMessage = isCoverLoadSuccess && !showDriverEntry
  const showLoadingScreen = showSuccessMessage || isLoadingStep1
  const showCoverloadActions = showLoadingScreen || !showDriverEntry
  const isLoadingState = isLoadingStep1 || isLoadingStep2
  const saveCallbackReminderCustom = !assignNow && !callbackRecommended

  const closeModalHandler = useCallback(() => {
    dispatch(clearCoverLoadModal(isFormSubmitSuccess))

    setCoverFields(undefined)
    setUpdateFields(undefined)
  }, [dispatch, isFormSubmitSuccess])

  useEffect(() => {
    setShowDriverEntry(isPendingCoveredStatus && isModalOpen)
  }, [isPendingCoveredStatus, isModalOpen])

  // When load is covered, show loading success state
  useEffect(() => {
    if (isCoverLoadSuccess) setTimeout(() => setShowDriverEntry(true), 2000)
  }, [isCoverLoadSuccess])

  // Close modal automatically only if notes were successfully submitted
  useEffect(() => {
    if (isNoteSubmitSuccess) {
      closeModalHandler()
    }
  }, [isNoteSubmitSuccess, closeModalHandler, dispatch])

  const saveCoverLoad = () => {
    if (!coverFields) return

    if (coverLoadError) {
      dispatch(clearCoverloadError())
    }

    dispatch(saveCarrierToCover(coverFields, shipmentId))
    trackEvent('Booking', 'COMPLETE_STEP_1', { shipmentId })
  }

  const notesBase = {
    createdById: user?.id,
    noteableId: shipmentId,
    noteableType: 'Shipment',
  }

  useEffect(() => {
    if (isFormSubmitSuccess) {
      const notesPayload = {
        body: updateFields?.notesBody,
        ...notesBase,
      }
      dispatch(submitNote(notesPayload))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormSubmitSuccess, dispatch])

  const updateCoverLoad = () => {
    if (!updateFields) return

    if (coverLoadError) {
      dispatch(clearCoverloadError())
    }

    if (assignNow) {
      dispatch(saveDriverToCover(updateFields, shipmentId))

      const assignDriverType = updateFields.selectedDriver?.id ? 'Existing' : 'New'
      trackEvent('Booking', 'COMPLETE_STEP_2', {
        shipmentId,
        driverAssigned: `${assignDriverType} Driver`,
      })
    } else {
      const callbackDate = updateFields?.callbackDate
      const callbackTime = updateFields?.callbackTime

      const notesWithCallbackPayload = {
        ...notesBase,
        body: updateFields?.notesBody,
        callbackDate,
        callbackTime,
      }
      dispatch(submitNote(notesWithCallbackPayload))

      trackEvent('Booking', 'COMPLETE_STEP_2', {
        shipmentId,
        callbackReminder: saveCallbackReminderCustom ? 'custom' : 'recommended',
        callbackDate,
        callbackTime,
      })
    }
  }

  const coverLoadAction: IModalAction = {
    label: 'Cover Load',
    type: 'confirm',
    disabled: isLoadingStep1 || !coverFields,
    action: saveCoverLoad,
    dataTest: 'save-cover-load',
  }

  const cancelAction: IModalAction = {
    label: 'Cancel',
    type: 'dismiss',
    disabled: isLoadingState,
    dataTest: 'cancel-cover-load',
  }

  const saveAction: IModalAction = {
    label: 'Save',
    type: 'confirm',
    disabled: isLoadingStep2 || !updateFields,
    action: updateCoverLoad,
    dataTest: 'update-cover-load',
  }

  return (
    <FormModal
      title={`Cover Shipment #${shipmentId}`}
      size={10}
      open={isModalOpen}
      onClose={closeModalHandler}
      error={coverLoadError || notesCallbackError}
      dataTest='cover-flow-modal'
      className='cover-flow'
      actions={[cancelAction, { ...(showCoverloadActions ? coverLoadAction : saveAction) }]}>
      <>
        {/* Main Content */}
        <div className='cover-flow__main'>
          {showCarrierEntry && <CarrierEntryForm setCoverFields={setCoverFields} />}
          {showDriverEntry && (
            <DriverAssignForm
              setUpdateFields={setUpdateFields}
              setAssignNow={selectAssignOption}
              assignNow={assignNow}
              setCallbackReminder={selectCallbackOption}
              callbackRecommended={callbackRecommended}
            />
          )}
          {showLoadingScreen && <LoadingScreen isLoading={isLoadingStep1} />}
        </div>
        {/* Side Panel */}
        <div className='cover-flow__side'>{shipmentId && <CardPanel />}</div>
      </>
    </FormModal>
  )
}

export default CoverLoads
